import {
  doneBy,
  eventsUrl,
  getActivity,
  parseEntry,
  parseOnline,
  type Activity as Feed,
} from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSeen } from '../../app/live';

const locales: Record<string, string> = { et: 'et-EE' };

/** `12 min ago`, `yesterday`: how long ago `at` was, in the UI language. */
export function ago(at: string, now: number, language: string): string {
  const seconds = Math.round((Date.parse(at) - now) / 1000);
  const words = new Intl.RelativeTimeFormat(locales[language] ?? 'en-GB', {
    numeric: 'auto',
    style: 'short',
  });
  const steps: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
  ];
  let value = seconds;
  for (const [size, unit] of steps) {
    if (Math.abs(value) < size) return words.format(value, unit);
    value = Math.round(value / size);
  }
  return words.format(value, 'day');
}

/**
 * What the people on a trip did, newest first, and who has it open now — both as they happen,
 * from the trip's event stream.
 */
export function Activity({ trip }: { trip: string }) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const [feed, setFeed] = useState<Feed | null>(null);
  // When the feed last changed: the times shown are counted from it.
  const [now, setNow] = useState(0);

  // Read again each time it is seen: in the background it keeps no stream.
  const seen = useSeen();
  useEffect(() => {
    if (!seen) return;
    getActivity(trip).then(
      (read) => {
        setNow(Date.now());
        setFeed(read);
      },
      () => undefined,
    );
    const source = new EventSource(eventsUrl(trip));
    source.addEventListener('activity', (event) => {
      const entry = parseEntry(JSON.parse(String(event.data)));
      setNow(Date.now());
      setFeed((was) => (was ? { ...was, entries: [entry, ...was.entries] } : was));
    });
    source.addEventListener('online', (event) => {
      const online = parseOnline(JSON.parse(String(event.data)));
      setFeed((was) => (was ? { ...was, online } : was));
    });
    return () => {
      source.close();
    };
  }, [trip, seen]);

  if (feed === null) return null;
  return (
    <section className="panel activity" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`}>{t('activity.title')}</h2>
      {feed.online.length > 0 && (
        <p className="online">
          <span className="online-dot" aria-hidden="true" />
          {t('activity.online', { names: feed.online.map((p) => p.name).join(', ') })}
        </p>
      )}
      {feed.entries.length === 0 ? (
        <p className="quiet">{t('activity.none')}</p>
      ) : (
        <ul className="activity-list">
          {feed.entries.map((entry) => {
            const name = doneBy(entry, t('activity.someone'));
            return (
              <li key={entry.id}>
                <span>
                  {t(`activity.code.${entry.code.replace('.', '_')}`, {
                    name,
                    about: entry.about,
                    defaultValue: t('activity.changed', { name }),
                  })}
                </span>
                <span className="quiet"> · {ago(entry.at, now, i18n.language)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
